import React from "react";
import _ from "lodash";
import moment from "moment";
import submit from "submit-form";
import DateInput from "../../../parts/inputs/date-input";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import ShareModal from "../../../parts/modals/share";
import getDayRangeString from "../../../utils/day-range-string";
import MetricsBackButton from "./metrics-back-button";
import set from "@rill/set";

import { PRIMARIES, DATE_FORMATS } from "../../../constants";
import NavigationComponent from "../calendar/components/NavigationComponent";
import SvgIcon from "../../../parts/SvgIcon";
import { AthleteLinks } from "../../../parts/athlete-links";
import { AthleteSelect } from "../../../parts/inputs/athlete-select-new";
import getAvatarUrl from "../../../utils/get-avatar-url";
import { ReportsBackHandler } from "../calendar/components/ReportsBackHandler";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { useTranslation } from "react-i18next";
import { isAdminOrOwner } from "../../../utils/organization-permissions-helpers";

const { DATE_PICKER_FORMAT, DATE_DAY_FORMAT } = DATE_FORMATS;

function View(props) {
  const { locals } = useRillContext();
  const { t } = useTranslation("athlete-portal");

  const { query, search, pathname, athlete: athleteObj } = locals;
  const { units, athlete } = query;
  const organizationCoach = query.organizationCoach || undefined;
  const organizationCoachParam = organizationCoach
    ? `&organizationCoach=${organizationCoach}`
    : "";
  const exercisesRecordsLoad =
    query.type === "total"
      ? locals.exercisesRecordsLoad
      : _.pick(locals.exercisesRecordsLoad, [query.type]);

  return (
    <main role="reports-wrapper" className="Main Metrics">
      <NavigationComponent
        query={query}
        pathName={"/athlete-portal/metrics"}
        textKey="nav.calendarDayView"
        classNames="mobile-hidden"
      />
      <div className="athlete-section-header only-show-mobile">
        <AthleteLinks
          athlete={athleteObj}
          pathName={pathname}
          classNames={"only-show-mobile"}
          query={query}
        />
        <div className="athlete-select dropdown-athlete">
          <div
            className="avatar"
            style={{
              backgroundImage: `url(${getAvatarUrl(athlete)})`,
            }}
          />
          <AthleteSelect locals={locals} />
        </div>
      </div>
      <article role="report-content" id="strength-metrics">
        <ReportsBackHandler
          title={t("metrics.strengthMetrics")}
          locals={locals}
          customBackUrl={`/athlete-portal/metrics/${search}`}
        />
        <header>
          <hgroup className="metrics-title">
            <MetricsBackButton
              url={`/athlete-portal/metrics/${search}`}
              classNames="mobile-hidden"
            />
            <SvgIcon
              src="/img/new-icons/personal-record-ico.svg"
              className="metrics-icon mobile-hidden"
            />
            <div>
              <SvgIcon
                src="/img/new-icons/personal-record-ico.svg"
                className="metrics-icon only-show-mobile"
              />
              <h3 className="record-table alt">{t("metrics.personalRecordTable")}</h3>
              <ToggleLinksInput
                name="units"
                options={[
                  { text: t("metricsDrawer.kg"), value: "metric" },
                  { text: t("metricsDrawer.lbs"), value: "imperial" },
                ]}
                className={"only-show-mobile"}
              />
              <p className="p4 mobile-hidden">
                {t("personalRecordTable.description")}
              </p>
            </div>
            <p className="p4 only-show-mobile">
              {t("personalRecordTable.description")}
            </p>
          </hgroup>
          <hgroup className="metrics-dates">
            <div role="calendar-date-selectors">
              <DateInput
                name="start"
                placeholder={t("reports.startDate")}
                value={query.start}
                max={query.end}
                onChange={(date) => handleDayChange(date, "start")}
              />
              <span>{t("reports.to")}</span>
              <DateInput
                name="end"
                placeholder={t("reports.endDate")}
                value={query.end}
                min={query.start}
                onChange={(date) => handleDayChange(date, "end")}
                alignRight={true}
              />
            </div>
          </hgroup>
          <hgroup className="metrics-data-options">
            <ToggleLinksInput
              name="units"
              options={[
                { text: t("metricsDrawer.kg"), value: "metric" },
                { text: t("metricsDrawer.lbs"), value: "imperial" },
              ]}
              className={"mobile-hidden"}
            />

            <ToggleLinksInput
              className="tabs"
              name="type"
              options={[
                { text: t("strengthMetricsShared.total"), value: "total" },
                { text: t("strengthMetricsShared.squat"), value: "squat" },
                { text: t("strengthMetricsShared.bench"), value: "bench" },
                { text: t("strengthMetricsShared.deadlift"), value: "deadlift" },
              ]}
            />
          </hgroup>
        </header>
        <section role="table-wrapper">
          <table role="report-table">
            <thead>
              <tr>
                <th>
                  <span className="left">{t("personalRecordTable.exerciseHeader")}</span>
                  <span className="right">{t("personalRecordTable.repsHeader")}</span>
                </th>
                <th>1</th>
                <th>2</th>
                <th>3</th>
                <th>4</th>
                <th>5</th>
                <th>6</th>
                <th>7</th>
                <th>8</th>
                <th>9</th>
                <th>10</th>
                <th>11</th>
                <th>12</th>
              </tr>
            </thead>
            <tbody>
              {_.flatMap(PRIMARIES, (primary) => {
                const secondaries = exercisesRecordsLoad[primary];
                return _(secondaries)
                  .map((reps, secondary) => {
                    return { reps, secondary };
                  })
                  .sortBy("secondary")
                  .map(({ reps, secondary }) => {
                    const secondaryLabel = secondary ? `(${secondary})` : "";
                    return (
                      <tr key={primary + secondary}>
                        <td>
                          {_.capitalize(primary)} {secondaryLabel}
                        </td>
                        {_.map(_.range(1, 13), (i) => {
                          const load = _.get(reps[i], "load");
                          const date = moment.utc(_.get(reps[i], "date"));
                          if (!load) return <td key={i} />;
                          return (
                            <td key={i}>
                              <a
                                href={
                                  `/${
                                    athlete
                                      ? `athlete-portal/calendar/?athlete=${athlete}&`
                                      : "dashboard/calendar/?"
                                  }view=day&date=${date.format(
                                    DATE_DAY_FORMAT
                                  )}` + organizationCoachParam
                                }
                                className="metrics-table-link"
                              >
                                {_.round(
                                  locals.fromMetric(load, units, false),
                                  1
                                )}
                              </a>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                  .value();
              })}
            </tbody>
          </table>
        </section>
      </article>
    </main>
  );
}

function handleDayChange(date, field) {
  submit("/actions/refresh", {
    method: "GET",
    body: { [field]: date },
  });
}
const RecordTable = (app) => {
  app.get(
    "/record-table",
    set("heading", "Personal Record Table"),
    async ({ req, res, load, locals, api, fail }) => {
      const isAllowToViewAllOrganizationsAthletes = isAdminOrOwner(
        locals.organizationRoleName
      );

      if (
        locals.activeUser.organizationId &&
        isAllowToViewAllOrganizationsAthletes &&
        !locals.allAthletesFromOrganization
      ) {
        await load("allAthletesFromOrganization");
      }

      const { query } = req;
      const user = locals.activeUser._id;
      _.defaults(query, {
        type: "total",
        units: locals.units,
        start: moment.utc().startOf("month").format(DATE_PICKER_FORMAT),
        end: moment.utc().endOf("month").format(DATE_PICKER_FORMAT),
      });

      await load("exercisesRecordsLoad", {
        start: moment
          .utc(query.start, DATE_PICKER_FORMAT)
          .startOf("day")
          .toDate(),
        end: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
        proxyAthlete: query.athlete,
        reqSource: "metrics",
      });

      const modal = _.get(query, "modal", {});

      if (modal.type === "share" && !modal.snapshot) {
        // create share link
        try {
          modal.snapshot = (
            await (
              await api("snapshot", {
                method: "POST",
                body: {
                  user: {
                    name: locals.athlete.name,
                    registered: locals.athlete.registered,
                    avatar: locals.athlete.avatar,
                    units: locals.athlete.units,
                  },
                  type: "record-table",
                  data: JSON.stringify(
                    _.chain(locals)
                      .pick(["exercisesRecordsLoad"])
                      .assign({ start: query.start, end: query.end })
                      .value()
                  ),
                },
              })
            ).json()
          )._id;

          modal.date = getDayRangeString([
            moment.utc(query.start, DATE_PICKER_FORMAT),
            moment.utc(query.end, DATE_PICKER_FORMAT),
          ]);
          return res.refreshWith({ modal });
        } catch (e) {
          console.error(e.stack || e);
          fail(400, "Error creating share link.");
        }
      }

      locals.modal = {
        share: <ShareModal />,
        "activity-feed": <ActivityDrawer />,
      }[modal.type];

      res.body = <View />;
    }
  );
};

export default RecordTable;
